import {
  type Face,
  INK,
  MARK,
  MASK_BODY,
  type Pose,
  body,
  dot,
  eye,
  gladFace,
  shut,
} from '../face-kit';
import { TAU, type Ctx, circle } from '../paint';
import { FLAME, blinking, flamePulse, sunTurn } from './canicule-moments';

// The whole sun, flames out, fits the mask cell at this scale.
const SCALE = 0.9;
const RING = 39;
const FACE = 31;
const THIRD_EYE = { y: -20, rx: 2.6, ry: 4.6 };
const TINTS = ['rgb(255 255 255 / 0.35)', 'rgb(255 255 255 / 0.3)', 'rgb(255 255 255 / 0.25)'];
const LIPS = 'rgb(255 255 255 / 0.18)';

function pol(radius: number, angle: number): [number, number] {
  return [Math.cos(angle) * radius, Math.sin(angle) * radius];
}

function flame(ctx: Ctx, angle: number, length: number, from = FLAME.from, width = 0.25): void {
  ctx.moveTo(...pol(from, angle - width));
  ctx.bezierCurveTo(
    ...pol(from + length * 0.4, angle - width * 0.6 + 0.06),
    ...pol(from + length * 0.75, angle + 0.1),
    ...pol(from + length, angle + 0.15),
  );
  ctx.bezierCurveTo(
    ...pol(from + length * 0.7, angle + 0.13),
    ...pol(from + length * 0.35, angle + width * 0.9),
    ...pol(from, angle + width),
  );
  ctx.closePath();
}

function shortRay(ctx: Ctx, angle: number, length: number): void {
  const from = FLAME.from;
  ctx.moveTo(...pol(from, angle - 0.12));
  ctx.quadraticCurveTo(
    ...pol(from + length * 0.6, angle - 0.02),
    ...pol(from + length, angle + 0.06),
  );
  ctx.quadraticCurveTo(...pol(from + length * 0.5, angle + 0.11), ...pol(from, angle + 0.12));
  ctx.closePath();
}

function stroke(ctx: Ctx, width: number, color: string, points: readonly [number, number][]) {
  ctx.beginPath();
  points.forEach(([x, y], index) => {
    if (index === 0) {
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
    }
  });
  ctx.lineWidth = width;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = color;
  ctx.stroke();
}

function flames(ctx: Ctx, length: number, turn: number, blink: boolean): void {
  const lengthOf = (index: number) => length * (index % 2 === 1 ? 0.86 : 1);
  body(ctx, () => {
    ctx.beginPath();
    for (let index = 0; index < FLAME.count; index += 1) {
      flame(ctx, (index / FLAME.count) * TAU + turn, lengthOf(index));
    }
  });
  for (let index = 0; index < FLAME.count; index += 1) {
    const angle = (index / FLAME.count) * TAU + turn;
    const reach = lengthOf(index);
    stroke(ctx, 1.4, TINTS[index % 3] ?? MARK, [
      pol(39, angle - 0.04),
      pol(FLAME.from + reach * 0.55, angle + 0.05),
      pol(FLAME.from + reach * 0.86, angle + 0.12),
    ]);
    const [ex, ey] = pol(FLAME.from + reach * 0.36, angle + 0.03);
    ctx.save();
    ctx.translate(ex, ey);
    ctx.rotate(angle + 0.03);
    ctx.beginPath();
    ctx.ellipse(0, 0, 3.8, blink ? 0.5 : 2, 0, 0, TAU);
    ctx.lineWidth = 1.3;
    ctx.strokeStyle = MARK;
    ctx.stroke();
    if (!blink) {
      dot(ctx, 0, 0, 1.1);
    }
    ctx.restore();
  }
  body(ctx, () => {
    ctx.beginPath();
    for (let index = 0; index < FLAME.count; index += 1) {
      shortRay(ctx, ((index + 0.5) / FLAME.count) * TAU + turn, length * 0.5);
    }
  });
}

function ring(ctx: Ctx, turn: number): void {
  body(ctx, () => {
    ctx.beginPath();
    circle(ctx, RING);
  });
  for (let index = 0; index < 24; index += 1) {
    const angle = (index / 24) * TAU - turn * 0.5;
    if (index % 2 === 1) {
      dot(ctx, ...pol(35.5, angle), 1.4, TINTS[1]);
    } else {
      stroke(ctx, 1.3, TINTS[0] ?? MARK, [
        pol(33.5, angle - 0.08),
        pol(37.5, angle),
        pol(33.5, angle + 0.08),
      ]);
    }
  }
}

function thirdEye(ctx: Ctx, color: string): void {
  ctx.beginPath();
  ctx.ellipse(0, THIRD_EYE.y, THIRD_EYE.rx, THIRD_EYE.ry, 0, 0, TAU);
  ctx.lineWidth = 1.8;
  ctx.strokeStyle = color;
  ctx.stroke();
}

function almondEye(ctx: Ctx, side: number): void {
  ctx.save();
  ctx.translate(side * 11, -1);
  const almond = () => {
    ctx.beginPath();
    ctx.moveTo(-7, 0);
    ctx.quadraticCurveTo(0, -5.5, 7, 0);
    ctx.quadraticCurveTo(0, 4.5, -7, 0);
  };
  almond();
  ctx.fillStyle = INK;
  ctx.fill();
  ctx.save();
  almond();
  ctx.clip();
  circle(ctx, 3, side * 0.8, 0.8);
  ctx.lineWidth = 1.8;
  ctx.strokeStyle = MARK;
  ctx.stroke();
  dot(ctx, side * 0.8, 0.8, 1.2);
  ctx.fillStyle = MASK_BODY;
  ctx.fillRect(-8, -6, 16, 5.2);
  ctx.restore();
  almond();
  ctx.lineWidth = 2;
  ctx.strokeStyle = MARK;
  ctx.stroke();
  stroke(ctx, 2.6, MARK, [
    [-7, -0.6],
    [0, -1.2],
    [7, -0.6],
  ]);
  ctx.restore();
}

function canicule(ctx: Ctx, pose: Pose): void {
  const beats = pose.t * 4;
  ctx.scale(SCALE, SCALE);
  const turn = sunTurn(pose.t);
  flames(ctx, FLAME.length + FLAME.pulse * flamePulse(beats), turn, blinking(beats));
  ring(ctx, turn);
  body(ctx, () => {
    ctx.beginPath();
    circle(ctx, FACE);
  });
  thirdEye(ctx, MARK);
  stroke(ctx, 1.8, MARK, [
    [-2.6, THIRD_EYE.y],
    [2.6, THIRD_EYE.y],
  ]);
  ctx.beginPath();
  ctx.moveTo(-22, -7);
  ctx.quadraticCurveTo(-12, -16, -2.5, -8);
  ctx.quadraticCurveTo(-1, 0, -4, 6);
  ctx.quadraticCurveTo(0, 9, 4, 6);
  ctx.quadraticCurveTo(1, 0, 2.5, -8);
  ctx.quadraticCurveTo(12, -16, 22, -7);
  ctx.lineWidth = 3;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = MARK;
  ctx.stroke();
  for (const side of [-1, 1]) {
    if (shut(pose)) {
      eye(ctx, pose, side * 11, -1, 6);
    } else {
      almondEye(ctx, side);
    }
    const spiral: [number, number][] = [];
    for (let step = 0; step <= 16; step += 1) {
      const angle = step * 0.55;
      const radius = 4.6 * (1 - step / 20);
      spiral.push([side * 20 + Math.cos(angle) * radius * side, 9 + Math.sin(angle) * radius]);
    }
    stroke(ctx, 1.3, TINTS[0] ?? MARK, spiral);
  }
  ctx.beginPath();
  ctx.moveTo(-10, 14);
  ctx.quadraticCurveTo(-5, 10.5, 0, 12.6);
  ctx.quadraticCurveTo(5, 10.5, 11, 12.5);
  ctx.quadraticCurveTo(1, 22, -10, 14);
  ctx.closePath();
  ctx.fillStyle = LIPS;
  ctx.fill();
  ctx.lineWidth = 2.4;
  ctx.strokeStyle = MARK;
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(-10, 14);
  ctx.quadraticCurveTo(0, 16.8, 11, 12.5);
  ctx.lineWidth = 1.8;
  ctx.stroke();
  ctx.save();
  ctx.globalAlpha = 1 - pose.t * 0.7;
  ctx.translate(24, -15 + pose.t * 12);
  ctx.beginPath();
  ctx.moveTo(0, -4.5);
  ctx.quadraticCurveTo(4, 1, 0, 3.6);
  ctx.quadraticCurveTo(-4, 1, 0, -4.5);
  ctx.fillStyle = MARK;
  ctx.fill();
  ctx.restore();
}

function caniculeGlad(ctx: Ctx): void {
  ctx.scale(SCALE, SCALE);
  gladFace(ctx, { eyeY: -1, eyeX: 11, mouthY: 11, mouthWidth: 11, part: 'smile' });
  thirdEye(ctx, INK);
  dot(ctx, 0, THIRD_EYE.y, 1.8, INK);
}

export const THIRD_EYE_AT: readonly [number, number] = [0, THIRD_EYE.y * SCALE];

export const CANICULE_FACE: Face = {
  draw: canicule,
  glad: caniculeGlad,
  frames: 16,
  loopBeats: 4,
  boss: true,
};
